// Localização: frontend/src/hooks/useLotes.js (versã 0.2.3)

import { useState, useEffect, useCallback } from 'react';
// 💡 Importa mutações E a nova função de consulta
import { get, post, put, remove, getLotesDisponiveisParaPlanoApi } from '../api/api'; 
import { usePermissions } from '../context/PermissionContext';
import { useAuth } from '../context/AuthContext'; 

/**
 * Hook customizado para gerenciar todas as operações de Lotes de medicamentos e Alertas.
 * @returns {object} Estado e funções para interagir com a API de Lotes.
 */
export const useLotes = () => {
    const { isAuthenticated } = useAuth();
    const { checkPermission } = usePermissions();

    // ------------------------------------
    // ESTADOS DE DADOS
    // ------------------------------------
    const [lotes, setLotes] = useState([]);          // Lista principal de lotes (para página de gestão de estoque)
    const [alertas, setAlertas] = useState([]);      // Lista de alertas de estoque
    const [lotesParaPlano, setLotesParaPlano] = useState([]); // 💡 NOVO: Lotes filtrados para o formulário de Plano Médico

    // ------------------------------------
    // ESTADOS DE CARREGAMENTO E ERRO
    // ------------------------------------
    const [isLoadingLotes, setIsLoadingLotes] = useState(false);
    const [isLoadingAlertas, setIsLoadingAlertas] = useState(false);
    const [isLoadingLotesPlano, setIsLoadingLotesPlano] = useState(false); // 💡 NOVO: Carregamento para lotes de plano
    const [error, setError] = useState(null);

    // Permissões
    const canReadLotes = checkPermission('estoque', 'can_read');

    // -------------------------------------------------------------------
    // FUNÇÕES DE BUSCA (READ)
    // -------------------------------------------------------------------

    /**
     * Busca todos os lotes (opcionalmente filtrados por medicamento/disponibilidade).
     */
    const fetchLotes = useCallback(async (medicamentoId = null, disponivel = false) => {
        if (!isAuthenticated || !canReadLotes) return;

        setIsLoadingLotes(true);
        setError(null);
        try {
            let url = '/lotes';
            const params = [];

            if (medicamentoId) {
                params.push(`medicamento_id=${medicamentoId}`);
            }
            if (disponivel) {
                params.push(`disponivel=${disponivel}`);
            }
            
            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }

            const response = await get(url);
            setLotes(response.data.data || []);
        } catch (err) {
            console.error("Erro ao buscar lotes:", err);
            setError(err.response?.data?.message || "Falha ao carregar lotes.");
            setLotes([]);
        } finally {
            setIsLoadingLotes(false);
        }
    }, [isAuthenticated, canReadLotes]);
    
    /**
     * Busca os alertas de validade/estoque dos lotes.
     */
    const fetchAlertas = useCallback(async () => {
        if (!isAuthenticated || !canReadLotes) return;

        setIsLoadingAlertas(true);
        setError(null);
        try {
            const response = await get('/lotes/alertas'); 
            setAlertas(response.data.data || []);
        } catch (err) {
            console.error("Erro ao buscar alertas:", err);
            setError(err.response?.data?.message || "Falha ao carregar alertas.");
            setAlertas([]);
        } finally {
            setIsLoadingAlertas(false);
        }
    }, [isAuthenticated, canReadLotes]);

    /**
     * 💡 NOVO: Busca lotes disponíveis para seleção em um Plano Médico.
     * @param {number} medicamentoId - ID do medicamento.
     * @param {number} pacienteId - ID do paciente.
     */
    const fetchLotesDisponiveisParaPlano = useCallback(async (medicamentoId, pacienteId) => {
        if (!isAuthenticated || !canReadLotes || !medicamentoId || !pacienteId) {
            setLotesParaPlano([]);
            return;
        }

        setIsLoadingLotesPlano(true);
        setError(null);
        try {
            // Usa a função de API que criamos no api.js
            const response = await getLotesDisponiveisParaPlanoApi(medicamentoId, pacienteId);
            setLotesParaPlano(response.data.data || []);
        } catch (err) {
            console.error("Erro ao buscar lotes para plano:", err);
            // Define o erro no estado do hook para ser exibido no formulário, se necessário
            setError(err.response?.data?.message || "Falha ao carregar lotes disponíveis.");
            setLotesParaPlano([]);
        } finally {
            setIsLoadingLotesPlano(false);
        }
    }, [isAuthenticated, canReadLotes]);


    // -------------------------------------------------------------------
    // FUNÇÕES DE MUTAÇÃO (CRUD) - Lógica inalterada
    // -------------------------------------------------------------------

    const createLote = async (data) => {
        try {
            const response = await post('/lotes', data); 
            return response.data;
        } catch (err) {
            throw new Error(err.response?.data?.message || 'Falha ao cadastrar lote.');
        }
    };
    
    const updateLote = async (id, data) => {
        try {
            const response = await put(`/lotes/${id}`, data); 
            return response.data;
        } catch (err) {
            throw new Error(err.response?.data?.message || 'Falha ao atualizar lote.');
        }
    };
    
    const deleteLote = async (id) => {
        try {
            await remove(`/lotes/${id}`); 
        } catch (err) {
            throw new Error(err.response?.data?.message || 'Falha ao excluir lote.');
        }
    };

    const cancelarLote = async (id) => {
        try {
            const response = await put(`/lotes/${id}/cancelar`, {}); 
            return response.data;
        } catch (err) {
            throw new Error(err.response?.data?.message || 'Falha ao cancelar lote.');
        }
    };


    // -------------------------------------------------------------------
    // RETORNO PÚBLICO
    // -------------------------------------------------------------------

    return {
        lotes,
        alertas,
        lotesParaPlano, // 💡 NOVO
        
        isLoadingLotes,
        isLoadingAlertas,
        isLoadingLotesPlano, // 💡 NOVO
        error, 
        
        // Funções de busca
        fetchLotes,
        fetchAlertas,
        fetchLotesDisponiveisParaPlano, // 💡 NOVO
        
        // Funções CRUD
        createLote,
        updateLote,
        deleteLote,
        cancelarLote
    };
};